import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Plus, X } from 'lucide-react';
import api from '../services/api';

const statusLabel = {
  NUEVO: 'Nuevo', ASIGNADO: 'Asignado', EN_PROCESO: 'En proceso',
  EN_ESPERA: 'En espera', COMPLETADO: 'Completado', CANCELADO: 'Cancelado'
};
const statusColor = {
  NUEVO: { bg: '#eff6ff', text: '#2563eb' },
  ASIGNADO: { bg: '#fffbeb', text: '#d97706' },
  EN_PROCESO: { bg: '#fff7ed', text: '#ea580c' },
  EN_ESPERA: { bg: '#f5f3ff', text: '#7c3aed' },
  COMPLETADO: { bg: '#f0fdf4', text: '#16a34a' },
  CANCELADO: { bg: '#fef2f2', text: '#dc2626' },
};
const priorityColor = {
  BAJA: { bg: '#f1f5f9', text: '#64748b' },
  MEDIA: { bg: '#eff6ff', text: '#2563eb' },
  ALTA: { bg: '#fff7ed', text: '#ea580c' },
  CRITICA: { bg: '#fef2f2', text: '#dc2626' },
};

export default function SolicitudesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [solicitudes, setSolicitudes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({ status: '', priority: '', areaId: '' });
  const [form, setForm] = useState({ description: '', requesterName: '', areaId: '', priority: 'MEDIA' });
  const [submitting, setSubmitting] = useState(false);

  const fetchSolicitudes = async () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.areaId) params.areaId = filters.areaId;
    const res = await api.get('/requests', { params });
    setSolicitudes(res.data);
  };

  useEffect(() => {
    api.get('/areas').then(res => setAreas(res.data)).catch(() => {});
    fetchSolicitudes().finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSolicitudes(); }, [filters]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/requests', { ...form, areaId: Number(form.areaId) });
      setShowForm(false);
      setForm({ description: '', requesterName: '', areaId: '', priority: 'MEDIA' });
      fetchSolicitudes();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al crear solicitud');
    } finally { setSubmitting(false); }
  };

  const canCreate = ['ADMIN', 'DIRECTOR', 'GERENTE', 'SUPERVISOR'].includes(user?.role);

  if (loading) return <p style={{ color: '#64748b' }}>Cargando...</p>;

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>
      <style>{`
        .sol-filters { display: flex; flex-wrap: wrap; gap: 8px; }
        .sol-table-wrap { overflow-x: auto; }
        .sol-card { display: none; }
        @media (max-width: 768px) {
          .sol-table { display: none !important; }
          .sol-card { display: block !important; }
        }
      `}</style>

      {/* Modal nueva solicitud */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 8px 40px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a2f6b' }}>Nueva solicitud</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Descripción</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required rows={3}
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }}
                  placeholder="Describe el problema o solicitud..." />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Nombre del solicitante</label>
                <input type="text" value={form.requesterName} onChange={e => setForm({ ...form, requesterName: e.target.value })} required
                  style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Área</label>
                <select value={form.areaId} onChange={e => setForm({ ...form, areaId: e.target.value })} required
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                  <option value="">Selecciona un área</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Prioridad</label>
                <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                  style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit', outline: 'none' }}>
                  <option value="BAJA">Baja</option>
                  <option value="MEDIA">Media</option>
                  <option value="ALTA">Alta</option>
                  <option value="CRITICA">Crítica</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ flex: 1, padding: '11px', border: '1.5px solid #e2e8f0', borderRadius: 10, background: 'white', fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', color: '#64748b' }}>
                  Cancelar
                </button>
                <button type="submit" disabled={submitting}
                  style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: '#2563eb', color: 'white', fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                  {submitting ? 'Guardando...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2f6b', margin: 0 }}>Solicitudes</h1>
        {canCreate && (
          <button onClick={() => setShowForm(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#2563eb', color: 'white', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            <Plus size={15} /> Nueva solicitud
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="sol-filters" style={{ marginBottom: 16 }}>
        {[
          { value: filters.status, key: 'status', placeholder: 'Todos los estatus', options: Object.entries(statusLabel).map(([v, l]) => ({ v, l })) },
          { value: filters.priority, key: 'priority', placeholder: 'Todas las prioridades', options: [{ v: 'BAJA', l: 'Baja' }, { v: 'MEDIA', l: 'Media' }, { v: 'ALTA', l: 'Alta' }, { v: 'CRITICA', l: 'Crítica' }] },
          { value: filters.areaId, key: 'areaId', placeholder: 'Todas las áreas', options: areas.map(a => ({ v: String(a.id), l: a.name })) },
        ].map(f => (
          <select key={f.key} value={f.value} onChange={e => setFilters({ ...filters, [f.key]: e.target.value })}
            style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '8px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', background: 'white' }}>
            <option value="">{f.placeholder}</option>
            {f.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        ))}
      </div>

      {/* Tabla — desktop */}
      <div className="sol-table-wrap">
        <table className="sol-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'white', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 16px rgba(26,47,107,0.07)', border: '1px solid #e2e8f0' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Folio', 'Descripción', 'Solicitante', 'Área', 'Prioridad', 'Estatus', 'Asignado a'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', borderBottom: '1px solid #f1f5f9' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {solicitudes.length === 0 && <tr><td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>No hay solicitudes</td></tr>}
            {solicitudes.map(r => {
              const sc = statusColor[r.status] || {};
              const pc = priorityColor[r.priority] || {};
              return (
                <tr key={r.id} onClick={() => navigate(`/solicitudes/${r.id}`)}
                  style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  <td style={{ padding: '12px 16px' }}><span style={{ fontFamily: 'monospace', fontSize: 11, color: '#2563eb', fontWeight: 700 }}>{r.folio}</span></td>
                  <td style={{ padding: '12px 16px', maxWidth: 280 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</div></td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{r.requesterName}</td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{r.area?.name || '—'}</td>
                  <td style={{ padding: '12px 16px' }}><span style={{ background: pc.bg, color: pc.text, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{r.priority}</span></td>
                  <td style={{ padding: '12px 16px' }}><span style={{ background: sc.bg, color: sc.text, padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{statusLabel[r.status]}</span></td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{r.assignedTo?.name || 'Sin asignar'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Tarjetas — móvil */}
      <div className="sol-card">
        {solicitudes.length === 0 && <p style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>No hay solicitudes</p>}
        {solicitudes.map(r => {
          const sc = statusColor[r.status] || {};
          const pc = priorityColor[r.priority] || {};
          return (
            <div key={r.id} onClick={() => navigate(`/solicitudes/${r.id}`)}
              style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: 16, marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#2563eb', fontWeight: 700 }}>{r.folio}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ background: pc.bg, color: pc.text, padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{r.priority}</span>
                  <span style={{ background: sc.bg, color: sc.text, padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 600 }}>{statusLabel[r.status]}</span>
                </div>
              </div>
              <p style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, color: '#1e293b', lineHeight: 1.4 }}>{r.description}</p>
              <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#64748b', flexWrap: 'wrap' }}>
                <span>🏢 {r.area?.name || '—'}</span>
                <span>👤 {r.assignedTo?.name || 'Sin asignar'}</span>
                <span>📋 {r.requesterName}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
